// Must be first so it's imported before other styles that come from importing JS components.
import { InterfaceConnectingDialog } from 'Components/Equipment/Interfacing';
import AuthPage from 'Components/Pages/AuthPage';
import LogOut from 'Components/Pages/AuthPage/Logout';
import WorksheetEditorConflictResolutionDialog from 'Components/Worksheets/Core/WorksheetEditorConflictResolutionDialog';
import { useSerialPortDisconnectListener } from 'Lib/Hooks';
import React from 'react';
import { IconContext } from 'react-icons';
import { useSelector } from 'react-redux';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from 'react-router-dom';
import '../Core/Icon/Icon.scss';
import NoticeManager from '../Core/NoticeManager';
import { GridSyncErrorDialog, SyncErrorDialog } from '../Core/SyncErrorDialog';
import AddWorksheetPage from '../Pages/AddWorksheetPage';
import AdminPage from '../Pages/AdminPage/AdminPage';
import AgsExportPage from '../Pages/Ags/AgsExportPage';
import AgsProjectPage from '../Pages/Ags/ProjectPage';
import AgsWorkOrderListPage from '../Pages/Ags/WorkOrderGridPage';
import AgsWorkOrderPage from '../Pages/Ags/WorkOrderPage';
import BulkEntryHomePage from '../Pages/BulkEntryHomePage';
import EquipmentPage from '../Pages/EquipmentPages/EquipmentPage';
import GaugeStandardisationPage from '../Pages/EquipmentPages/NuclearGaugeStandardisationPage';
import Error404Page from '../Pages/Error404Page';
import HomePage from '../Pages/HomePage';
import InterLabPage from '../Pages/InterLabPage';
import SearchPage from '../Pages/SearchPage';
import TestPage from '../Pages/TestPage/TestPage';
import ToolkitPage from '../Pages/ToolkitPage';
import UserSettingsPage from '../Pages/UserSettingsPage';
import WorksheetBatchUpdatePage from '../Pages/WorksheetBatchUpdatePage';
import WorksheetGridLandingPage from '../Pages/WorksheetGridPage';
import WorksheetPage from '../Pages/WorksheetPage';
import { AppLayout } from './AppLayout';
import { AppPageComponent } from './AppPageComponent';
import { AuthRequiredLayout } from './AuthRequiredLayout';
import { EmbeddedInQESTFieldLayout } from './EmbeddedInQESTFieldLayout';
import { useAppTitleApplicator } from './useAppTitleApplicator';
import { useQestLabCallbacks } from './useQestLabCallbacks';

const isDevMode = process.env.REACT_APP_ENABLE_DEV_MODE === 'true';

/**
 * Component that handles app-wide "events"
 * by responding to a change in app state
 *
 * This returns a fragment and is isolated from the main app component
 * as changes to the state we are selecting will trigger a re-render of this component
 */
const AppEventHandler: React.FC = () => {
  useSerialPortDisconnectListener();
  useAppTitleApplicator();
  useQestLabCallbacks();

  return <></>;
};

const App: React.FC = () => {
  const adminSettingsClaim = useSelector(
    (state) => state.user.data?.Claims && state.user.data?.Claims['sq.administrator.settings']
  );
  const isQESTLab = useSelector(() => window.qest.userAgent?.includes('[QL]'));

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<AppLayout />}>
        {isDevMode && (
          <>
            <Route
              path="/toolkit/:component"
              element={
                <AppPageComponent pageTitle="Toolkit">
                  <ToolkitPage />
                </AppPageComponent>
              }
            />
            <Route path="/toolkit" element={<Navigate to="/toolkit/button" replace />} />
            <Route path="/test">
              <Route
                path=":component/hideSideBar"
                element={
                  <AppPageComponent pageTitle="Test Page">
                    <TestPage hideSideBar={true} />
                  </AppPageComponent>
                }
              />
              <Route
                path=":component?"
                element={
                  <AppPageComponent pageTitle="Test Page">
                    <TestPage />
                  </AppPageComponent>
                }
              />
            </Route>
            <Route element={<AuthRequiredLayout />}>
              <Route
                path="/addWorksheet"
                element={
                  <AppPageComponent pageTitle="Add Worksheet">
                    <AddWorksheetPage />
                  </AppPageComponent>
                }
              />
            </Route>
          </>
        )}
        <Route path="/qp" element={!isQESTLab && <EmbeddedInQESTFieldLayout />}>
          <Route
            path="/qp/logout"
            element={
              <AppPageComponent pageTitle="Log Out">
                <LogOut />
              </AppPageComponent>
            }
          />
          <Route
            path=""
            element={
              <AppPageComponent pageTitle="Log In">
                <AuthPage />
              </AppPageComponent>
            }
          />
        </Route>
        <Route element={<AuthRequiredLayout />}>
          <Route
            path="/worksheets/:worksheetId"
            element={
              <AppPageComponent pageTitle="Worksheet">
                <WorksheetPage />
              </AppPageComponent>
            }
          />
          <Route
            path="/worksheets/:worksheetId/snapshot/:snapshotId/"
            element={
              <AppPageComponent pageTitle="Worksheet">
                <WorksheetPage />
              </AppPageComponent>
            }
          />
          <Route path="/qfw/*" element={<EmbeddedInQESTFieldLayout />}>
            <Route
              path="worksheets/:worksheetId"
              element={
                <AppPageComponent pageTitle="Worksheet">
                  <WorksheetPage />
                </AppPageComponent>
              }
            />
          </Route>

          <Route
            path="/equipment/:equipmentId"
            element={
              <AppPageComponent pageTitle="Equipment">
                <EquipmentPage />
              </AppPageComponent>
            }
          />
          <Route
            path="/nuclearGaugeStandardization/:equipmentId"
            element={
              <AppPageComponent pageTitle="Nuclear Gauge Standardization">
                <GaugeStandardisationPage />
              </AppPageComponent>
            }
          />
          <Route
            path="/nuclearGaugeStandardization"
            element={
              <AppPageComponent pageTitle="Nuclear Gauge Standardization">
                <GaugeStandardisationPage />
              </AppPageComponent>
            }
          />
          <Route path="/qfw/*" element={<EmbeddedInQESTFieldLayout />}>
            <Route
              path="nuclearGaugeStandardization"
              element={
                <AppPageComponent pageTitle="Nuclear Gauge Standardization">
                  <GaugeStandardisationPage />
                </AppPageComponent>
              }
            />
          </Route>
          {adminSettingsClaim && (
            <>
              <Route
                path="/admin/:component/:elementId?"
                element={
                  <AppPageComponent pageTitle="Admin Settings">
                    <AdminPage />
                  </AppPageComponent>
                }
              />
              <Route path="/admin" element={<Navigate to="/admin/license-usage" replace />} />
            </>
          )}

          <Route
            path="/ags/workorders/:workOrderId"
            element={
              <AppPageComponent pageTitle="AGS Data">
                <AgsWorkOrderPage />
              </AppPageComponent>
            }
          />
          <Route
            path="/ags/workorders"
            element={
              <AppPageComponent pageTitle="AGS Work Orders">
                <AgsWorkOrderListPage />
              </AppPageComponent>
            }
          />
          <Route
            path="/ags/projects/:projectId"
            element={
              <AppPageComponent pageTitle="AGS Data">
                <AgsProjectPage />
              </AppPageComponent>
            }
          />
          <Route
            path="/ags/exports/:agsExportId"
            element={
              <AppPageComponent pageTitle="AGS Data">
                <AgsExportPage />
              </AppPageComponent>
            }
          />

          <Route
            path="/search"
            element={
              <AppPageComponent pageTitle="Search">
                <SearchPage />
              </AppPageComponent>
            }
          />

          <Route
            path="/user/*"
            element={
              <AppPageComponent pageTitle="User Settings">
                <UserSettingsPage />
              </AppPageComponent>
            }
          />

          <Route
            path="/batch-update/:typeId"
            element={
              <AppPageComponent pageTitle="Batch Update">
                <WorksheetBatchUpdatePage />
              </AppPageComponent>
            }
          />
          <Route
            path="/batch-update"
            element={
              <AppPageComponent pageTitle="Batch Update">
                <WorksheetBatchUpdatePage />
              </AppPageComponent>
            }
          />

          <Route
            path="/grid/:component"
            element={
              <AppPageComponent pageTitle="Bulk Entry">
                <WorksheetGridLandingPage />
              </AppPageComponent>
            }
          />
          <Route
            path="/grid"
            element={
              <AppPageComponent pageTitle="Bulk Entry">
                <BulkEntryHomePage />
              </AppPageComponent>
            }
          />

          <Route
            path="/inter-lab/:workOrderId?"
            element={
              <AppPageComponent pageTitle="Inter-Laboratory Testing">
                <InterLabPage />
              </AppPageComponent>
            }
          />

          <Route
            path="/"
            element={
              <AppPageComponent pageTitle="Home">
                <HomePage />
              </AppPageComponent>
            }
          />

          <Route
            path="/*"
            element={
              <AppPageComponent pageTitle="Page Not Found">
                <Error404Page />
              </AppPageComponent>
            }
          />
        </Route>
      </Route>
    ),
    { basename: process.env.PUBLIC_URL }
  );
  return (
    <IconContext.Provider value={{ className: 'Icon' }}>
      <AppEventHandler />
      <NoticeManager />
      <WorksheetEditorConflictResolutionDialog />
      <SyncErrorDialog />
      <GridSyncErrorDialog />
      <InterfaceConnectingDialog />

      {/*
      <Router basename={process.env.PUBLIC_URL}>

        <Routes>

        </Routes>
      </Router> */}
      <RouterProvider router={router} />
    </IconContext.Provider>
  );
};

export default App;
